import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAddPatient } from "../../contexts/AddPatientContext";
import { PatientAPI, searchPatients } from "../../services/patient.service";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import { db } from "../../db/schema";
import { unwrapFromStorage } from "../../db/encryption-bridge";
import { enqueue } from "../../services/offline-sync.service";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../../utils/network";
import { logger } from "../../utils/logger";
import { useRole } from "../../hooks/useRole";
import { getPatientAge } from "../../utils/age";

import Sidebar from "../../components/Sidebar";
import { AccessDenied } from "../../components/RequirePermission";
import { AppHeader } from "../../components/layout/AppHeader";
import { SearchBar, CategoryFilters, PatientTable, Patient, ManageTagsPanel } from "../../components/patients";
import { TagsAPI, ClinicTag } from "../../services/tags.service";

const mapDbPatientToPatient = (dbPatient: PatientDbRecord): Patient => {
  const avatarColors = ["#8B5CF6", "#00B8DB", "#FF006B", "#059669", "#F59E0B", "#2563EB", "#DC2626"];
  const randomColor = avatarColors[Math.floor(Math.random() * avatarColors.length)];

  // Extract contact number - handle both flat and nested structure
  const contactNumber = dbPatient.contact_number || dbPatient.contact?.contact_number || "N/A";
  const displayContact = contactNumber !== "N/A" ? contactNumber.replace(/^\+91/, "") : "N/A";

  return {
    id: dbPatient.patient_id || "",
    display_id: dbPatient.display_id || "",
    name: dbPatient.name || `${dbPatient.first_name} ${dbPatient.last_name}`,
    avatarColor: randomColor,
    consultationType: "Consultation",
    patientSince: new Date(dbPatient.created_at || Date.now()).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
    age: getPatientAge(dbPatient.date_of_birth, dbPatient.age_years),
    gender: dbPatient.gender,
    bloodGroup: dbPatient.blood_group || "N/A",
    contact: displayContact,
    lastVisit: new Date(dbPatient.updated_at || Date.now()).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
    status: dbPatient.is_active ? "Active" : "Inactive",
    tags: dbPatient.tags ?? [],
  };
};

const PatientListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { openAddPatientModal } = useAddPatient();
  // Legacy fallback true: New Patient was shown to every clinic role before RBAC,
  // so a backend that sends no grants must keep showing it. POST /patients
  // enforces patients.create either way.
  const { canOr } = useRole();
  const canCreatePatient = canOr("patients.create", true);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("search") ?? "");
  const [filterOption, setFilterOption] = useState("all");
  const [activeCategory, setActiveCategory] = useState("all");
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; patientId: string | null }>({ isOpen: false, patientId: null });
  const [restoreConfirmation, setRestoreConfirmation] = useState<{ isOpen: boolean; patientId: string | null }>({ isOpen: false, patientId: null });
  const [permanentDeleteConfirmation, setPermanentDeleteConfirmation] = useState<{ isOpen: boolean; patientId: string | null; error: string | null }>({ isOpen: false, patientId: null, error: null });
  // Set when the list GET comes back 403 — e.g. patients.view revoked while this
  // tab was open, so RequirePermission already let the route through on mount.
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  const [allTags, setAllTags] = useState<ClinicTag[]>([]);

  const hasFetchedRef = useRef(false);

  // The clinic's full tag vocabulary — every tag is a clickable filter pill
  // here, not just tags already present on the currently-loaded page of
  // patients, so a freshly-created (or 0-patient) tag is immediately visible
  // and clickable.
  const refreshTags = useCallback(() => {
    TagsAPI.list().then(setAllTags).catch(() => {});
  }, []);

  useEffect(() => {
    refreshTags();
  }, [refreshTags]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchPatients();
    }
  }, []);

  // Debounced server-side search - fetchPatients() below is capped at 50 rows
  // with no guaranteed order (see repository.list_patients), so a patient
  // outside that arbitrary first page is invisible to client-side filtering
  // alone, no matter what's typed. Mirrors the working pattern in
  // GlobalSearchBar.tsx / CreateBillPage.tsx.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const hits = await searchPatients(q, 50);
        if (!cancelled) setSearchResults(hits.map(mapDbPatientToPatient));
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchQuery]);

  // Check for navigation state to open Add Patient modal
  useEffect(() => {
    const state = location.state as { openAddModal?: boolean };
    // Gate the deep link too — /patients navigated to with openAddModal state must
    // not open the register form for someone who cannot create patients.
    if (state?.openAddModal && canCreatePatient) {
      openAddPatientModal();
      // Clear the state so modal doesn't reopen on refresh
      navigate("/patients", { replace: true, state: {} });
    }
  }, [location, navigate, openAddPatientModal, canCreatePatient]);

  // Listen for patient created event from global modal
  useEffect(() => {
    const handlePatientCreated = () => {
      fetchPatients();
    };

    window.addEventListener('patientCreated', handlePatientCreated as EventListener);
    return () => {
      window.removeEventListener('patientCreated', handlePatientCreated as EventListener);
    };
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      // "all" - the Active/Inactive/All dropdown below filters this client-side,
      // same as the existing search/category filters. The list endpoint used to
      // default to active-only, so switching to Inactive/All showed nothing.
      const response = await PatientAPI.getAll("all");
      logger.debug("Patient list fetched", { count: Array.isArray(response) ? response.length : 0 });
      const dbPatients = Array.isArray(response) ? response : [];
      const mappedPatients = dbPatients.map(mapDbPatientToPatient);
      setPatients(mappedPatients);
    } catch (error) {
      console.error("Error fetching patients:", error);

      // A 403 is an ANSWER from the server, not a connectivity failure — it proves
      // we are online. Falling through to the offline path below would render this
      // clinic's cached patients from IndexedDB under a "You are offline" banner,
      // i.e. show PHI the grant was just revoked for. Must stay ahead of the
      // isBackendUnreachable/IDB fallback.
      if (isPermissionDenied(error)) {
        setPatients([]);
        setAccessDenied(error instanceof Error ? error.message : null);
        return;
      }

      // Offline fallback: read from IndexedDB, scoped to the current user's clinic
      try {
        const currentUsers = await db.users.toArray();
        const clinicId = currentUsers[0]?.clinic_id;
        const rawRows = clinicId
          ? await db.patients.where("clinic_id").equals(clinicId).toArray()
          : await db.patients.toArray();
        if (rawRows.length > 0) {
          const decrypted = await Promise.all(
            rawRows.map(r => unwrapFromStorage<PatientDbRecord>(r as unknown as Record<string, unknown>)),
          );
          setPatients(decrypted.map(mapDbPatientToPatient));
          // SW converted the network failure into a 503 response, so
          // navigator.onLine stays true and the offline event never fired.
          // Dispatch it manually so OfflineBanner becomes visible.
          window.dispatchEvent(new Event("offline"));
          return;
        }
      } catch (dexieErr) {
        // OperationError = DEK mismatch (different user's encrypted rows).
        // TypeError / ApiError = DEK fetch failed (backend unreachable on cold start).
        // Any other error = Dexie or crypto failure.
        const isDekUnavailable = dexieErr instanceof TypeError ||
          (dexieErr instanceof Error && dexieErr.message?.toLowerCase().includes("fetch"));
        const isDecryptError = dexieErr instanceof Error &&
          (dexieErr.name === "OperationError" || dexieErr.message?.includes("decrypt"));
        if (isDekUnavailable) {
          toast.error("Offline — encryption key unavailable. Connect once to load the key, then you can go offline again.");
        } else if (isDecryptError) {
          toast.error("Offline data belongs to a different session. Please go online to reload.");
        } else {
          toast.error("Failed to load offline patients.");
        }
        return;
      }

      toast.error("No cached patients — go online to load patient list.");
      logger.error("Error fetching patients", error instanceof Error ? error : undefined);
    } finally {
      setLoading(false);
    }
  };

  // Filter patients based on search query, filter option, and category.
  // A real (>=2 char) query searches server-side across the whole clinic
  // (see the debounced effect above); below that, browse whatever the
  // initial capped fetch returned. Either way, status/category filters
  // still apply on top.
  const filteredPatients = useMemo(() => {
    const base = searchQuery.trim().length >= 2 ? searchResults : patients;
    return base.filter((patient) => {
      const matchesFilter =
        filterOption === "all" ||
        patient.status.toLowerCase() === filterOption.toLowerCase();

      // Category filter — matches against the patient's real tags, not the
      // (always-hardcoded, never-populated) consultationType field.
      const matchesCategory =
        activeCategory === "all" ||
        (patient.tags ?? []).some((t) => t.toLowerCase() === activeCategory.toLowerCase());

      return matchesFilter && matchesCategory;
    });
  }, [patients, searchResults, searchQuery, filterOption, activeCategory]);

  // Category pills reflect the clinic's full tag vocabulary (allTags), not
  // just tags present on the currently-loaded page of patients — a tag with
  // zero patients (or on a page not yet loaded) is still visible and
  // clickable here immediately after being created.
  const categories = useMemo(() => {
    const tagPills = allTags.map((t) => ({ id: t.name, label: t.name }));
    return [{ id: "all", label: "All" }, ...tagPills];
  }, [allTags]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredPatients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterOption, activeCategory]);

  // "Add other" both filters by the typed tag AND persists it as a standalone
  // clinic tag (TagsAPI.create is idempotent — case-insensitive — so retyping
  // an existing one is a no-op there). The created tag is merged straight
  // into local state instead of re-fetching the whole list — the create
  // response already has everything a pill needs, and re-fetching means a
  // second, much heavier round trip (the list endpoint scans every patient
  // to compute per-tag counts) before the new pill would appear.
  const handleAddCategory = (label: string) => {
    setActiveCategory(label.toLowerCase());
    TagsAPI.create(label)
      .then((tag) => {
        setAllTags((prev) =>
          prev.some((t) => t.tag_id === tag.tag_id) ? prev : [...prev, tag]
        );
      })
      .catch(() => {
        toast.error("Tag filtered here, but couldn't be saved. Please try again.");
      });
  };

  const handleDeletePatient = (patientId: string) => {
    setDeleteConfirmation({ isOpen: true, patientId });
  };

  // Returns true if the patient was never synced (pending), false if synced.
  // Cascade-purges all local data and queue entries for a pending patient so
  // no ghost pushes reach the server after a reconnect.
  const deletePatientOffline = async (patientId: string): Promise<boolean> => {
    const localRow = await db.patients.get(patientId);
    const isPending = (localRow as Record<string, unknown> | undefined)?.sync_status === "pending";

    await db.patients.delete(patientId);

    if (isPending) {
      // Patient never reached the server — cancel its create and cascade-delete
      // all locally-queued children so nothing orphaned is pushed on reconnect.
      await db.sync_queue.where("[entity_type+entity_id]").equals(["patient", patientId]).delete();

      const allergyIds    = (await db.allergies.where("patient_id").equals(patientId).primaryKeys())    as string[];
      const visitIds      = (await db.visits.where("patient_id").equals(patientId).primaryKeys())       as string[];
      const appointmentIds= (await db.appointments.where("patient_id").equals(patientId).primaryKeys()) as string[];

      const vitalsIds: string[]       = [];
      const prescriptionIds: string[] = [];
      for (const vid of visitIds) {
        const v = (await db.vitals.where("visit_id").equals(vid).primaryKeys())        as string[];
        const p = (await db.prescriptions.where("visit_id").equals(vid).primaryKeys()) as string[];
        vitalsIds.push(...v);
        prescriptionIds.push(...p);
      }

      await db.allergies.bulkDelete(allergyIds);
      await db.visits.bulkDelete(visitIds);
      await db.vitals.bulkDelete(vitalsIds);
      await db.prescriptions.bulkDelete(prescriptionIds);
      await db.appointments.bulkDelete(appointmentIds);

      const childPairs: Array<[string, string[]]> = [
        ["allergy",      allergyIds],
        ["visit",        visitIds],
        ["vitals",       vitalsIds],
        ["prescription", prescriptionIds],
        ["appointment",  appointmentIds],
      ];
      for (const [type, ids] of childPairs) {
        for (const id of ids) {
          await db.sync_queue.where("[entity_type+entity_id]").equals([type, id]).delete();
        }
      }
    } else {
      // Patient exists on server — enqueue soft-delete (DELETE /patients/{id}).
      // 404 on reconnect is treated as success (server already processed it).
      await enqueue("delete", "patient", patientId, {});
    }

    return isPending;
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.patientId) return;
    const patientId = deleteConfirmation.patientId;

    // Soft-delete only deactivates the patient - it stays in the list under
    // the Inactive filter (restorable) rather than disappearing entirely.
    const markInactive = () =>
      setPatients((prev) => prev.map((p) => (p.id === patientId ? { ...p, status: "Inactive" } : p)));

    if (!navigator.onLine) {
      try {
        const wasPending = await deletePatientOffline(patientId);
        if (wasPending) {
          setPatients((prev) => prev.filter((p) => p.id !== patientId));
        } else {
          markInactive();
        }
        toast.success(
          wasPending
            ? "Patient removed."
            : "Patient deactivated. Will sync when you reconnect.",
        );
      } catch (error) {
        logger.error("Error deleting patient offline", error instanceof Error ? error : undefined);
        toast.error("Failed to delete patient");
      }
      setDeleteConfirmation({ isOpen: false, patientId: null });
      return;
    }

    try {
      await PatientAPI.delete(patientId);
      markInactive();
      toast.success("Patient deactivated successfully");
      setDeleteConfirmation({ isOpen: false, patientId: null });
    } catch (error) {
      if (isBackendUnreachable(error)) {
        // Network dropped mid-request — fall back to offline delete
        try {
          const wasPending = await deletePatientOffline(patientId);
          if (wasPending) {
            setPatients((prev) => prev.filter((p) => p.id !== patientId));
          } else {
            markInactive();
          }
          toast.success("Patient deactivated. Will sync when you reconnect.");
        } catch (offlineErr) {
          logger.error("Error deleting patient offline", offlineErr instanceof Error ? offlineErr : undefined);
          toast.error("Failed to delete patient");
        }
      } else {
        // A 403 is not a failure — it is a refusal, and actionErrorMessage
        // surfaces the server's reason ("Your role does not include the 'Delete
        // Patients' permission.") which the user can actually act on. Don't log
        // it as an error either; nothing went wrong.
        if (!isPermissionDenied(error)) {
          logger.error("Error deleting patient", error instanceof Error ? error : undefined);
        }
        toast.error(actionErrorMessage(error, "Failed to delete patient"));
      }
      setDeleteConfirmation({ isOpen: false, patientId: null });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, patientId: null });
  };

  const handleRestorePatient = (patientId: string) => {
    setRestoreConfirmation({ isOpen: true, patientId });
  };

  const cancelRestore = () => {
    setRestoreConfirmation({ isOpen: false, patientId: null });
  };

  const confirmRestore = async () => {
    if (!restoreConfirmation.patientId) return;
    const patientId = restoreConfirmation.patientId;
    try {
      await PatientAPI.reactivate(patientId);
      setPatients((prev) => prev.map((p) => (p.id === patientId ? { ...p, status: "Active" } : p)));
      toast.success("Patient restored successfully");
    } catch (error) {
      logger.error("Error restoring patient", error instanceof Error ? error : undefined);
      toast.error("Failed to restore patient");
    }
    setRestoreConfirmation({ isOpen: false, patientId: null });
  };

  const handlePermanentDeletePatient = (patientId: string) => {
    setPermanentDeleteConfirmation({ isOpen: true, patientId, error: null });
  };

  const cancelPermanentDelete = () => {
    setPermanentDeleteConfirmation({ isOpen: false, patientId: null, error: null });
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeleteConfirmation.patientId) return;
    const patientId = permanentDeleteConfirmation.patientId;
    try {
      await PatientAPI.permanentlyDelete(patientId);
      setPatients((prev) => prev.filter((p) => p.id !== patientId));
      toast.success("Patient permanently deleted");
      setPermanentDeleteConfirmation({ isOpen: false, patientId: null, error: null });
    } catch (error) {
      // Surface the backend's specific reason (e.g. "has visits/invoices on
      // record") instead of a generic failure toast, since this is a real,
      // actionable answer to "why can't I delete this patient".
      const message = error instanceof Error ? error.message : "Failed to permanently delete patient.";
      logger.error("Error permanently deleting patient", error instanceof Error ? error : undefined);
      setPermanentDeleteConfirmation((prev) => ({ ...prev, error: message }));
    }
  };

  const handleViewPatient = (patient: Patient) => {
    navigate(`/patients/${patient.display_id || patient.id}`, { state: { patient } });
  };

  // Grant revoked while this tab was open: show the refusal rather than an empty
  // list, which would read as "this clinic has no patients".
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#f5f5f7]">
        <Sidebar />
        <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 min-h-screen">
          <AppHeader title="Patients" />
          <AccessDenied label="Patients" detail={accessDenied} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Sidebar />

      {/* Content offset for fixed sidebar */}
      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 min-h-screen">
      <AppHeader title="Patients" />
      {/* Main Content */}
      <div className="flex-1 min-w-0 px-2 py-2 md:p-4">
        <div className="mx-auto max-w-6xl">
          {/* Header with Search and Action Buttons */}
          <div className="mb-4 md:mb-6 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-4">
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              filterOption={filterOption}
              onFilterChange={setFilterOption}
            />

            {canCreatePatient && (
              <div className="flex items-center gap-2 md:gap-3">
                <button
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 md:flex-none md:px-4 md:py-2.5 md:text-sm"
                  onClick={() => openAddPatientModal()}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="md:h-4 md:w-4">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                  <span className="whitespace-nowrap">New Patient</span>
                </button>
              </div>
            )}
          </div>

          {/* Category Filters */}
          <div className="mb-4 md:mb-6 flex flex-wrap items-center justify-between gap-2">
            <div className="overflow-x-auto scrollbar-hide">
              <CategoryFilters
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
                onAddCategory={handleAddCategory}
              />
            </div>
            <ManageTagsPanel onTagsChanged={() => { fetchPatients(); refreshTags(); }} />
          </div>

          {/* Patient Table */}
          {loading || searching ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-slate-500">Loading patients...</div>
            </div>
          ) : (
            <>
              <PatientTable
                patients={paginatedPatients}
                onDelete={handleDeletePatient}
                onRestore={handleRestorePatient}
                onPermanentDelete={handlePermanentDeletePatient}
                onView={handleViewPatient}
                filterOption={filterOption}
                onFilterChange={setFilterOption}
              />

              {/* Pagination */}
              {filteredPatients.length > 0 && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-y-3 border-t border-slate-200 pt-4">
                  <div className="text-sm text-slate-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredPatients.length)} of {filteredPatients.length} patients
                  </div>
                  <div className="flex items-center gap-2 max-w-full">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="flex-shrink-0 rounded-lg border border-slate-200 bg-white px-3 min-h-[44px] text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1 overflow-x-auto">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-h-[44px] min-w-[44px] flex-shrink-0 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === page
                              ? "bg-violet-600 text-white"
                              : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="flex-shrink-0 rounded-lg border border-slate-200 bg-white px-3 min-h-[44px] text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={cancelDelete}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full mx-4 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-red-100">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#DC2626"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-slate-800 text-center mb-2">
              Delete Patient?
            </h3>
            <p className="text-slate-600 text-center mb-6">
              This patient will be deactivated and moved to the Inactive list. You can restore
              them from there at any time — this does not erase their record.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {restoreConfirmation.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={cancelRestore}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full mx-4 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-slate-800 text-center mb-2">
              Restore Patient?
            </h3>
            <p className="text-slate-600 text-center mb-6">
              This patient will be marked Active again and reappear in the main patient list.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelRestore}
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRestore}
                className="flex-1 px-4 py-3 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {permanentDeleteConfirmation.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={cancelPermanentDelete}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full mx-4 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-red-100">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-slate-800 text-center mb-2">
              Permanently Delete Patient?
            </h3>
            <p className="text-slate-600 text-center mb-2">
              This <span className="font-semibold text-red-600">erases the patient record entirely</span> — unlike
              regular delete, this cannot be undone or restored.
            </p>
            <p className="text-slate-500 text-sm text-center mb-6">
              Only allowed when the patient has no visits, appointments, or invoices on record.
            </p>
            {permanentDeleteConfirmation.error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3 mb-4 text-center">
                {permanentDeleteConfirmation.error}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={cancelPermanentDelete}
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmPermanentDelete}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes scale-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
      </div>
    </div>
  );
};

export default PatientListPage;
